import { Link as RouterLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTranslation } from 'react-i18next'

import { adminApi, opsApi } from '../../api/endpoints'
import { useFormatters } from '../../hooks/useFormatters'
import { EmptyState, ErrorState } from '../../components/common/States'

/**
 * What the owner sees first.
 *
 * Only two things belong here: requests waiting on a decision, and who arrives next. Anything
 * else is a number the owner would have to interpret, and this console is opened between
 * other jobs rather than studied.
 */
export function AdminDashboard() {
  const { t } = useTranslation()
  const { dateShort, moneyPrecise } = useFormatters()

  const pending = useQuery({
    queryKey: ['admin', 'bookings', 'PENDING', 0],
    queryFn: () => adminApi.bookings({ status: 'PENDING', page: 0, size: 10 }),
  })

  const upcoming = useQuery({
    queryKey: ['admin', 'bookings', 'CONFIRMED', 0],
    queryFn: () => adminApi.bookings({ status: 'CONFIRMED', page: 0, size: 10 }),
  })

  const feeds = useQuery({ queryKey: ['admin', 'feeds'], queryFn: adminApi.icalFeeds })
  const failingFeeds = (feeds.data ?? []).filter((feed) => feed.lastStatus === 'FAILED')

  // One request for everything the estate is doing today. Computed in SQL rather than
  // assembled from six list endpoints the browser would then have to count.
  const ops = useQuery({ queryKey: ['admin', 'ops', 'dashboard'], queryFn: opsApi.dashboard })
  const summary = ops.data

  // A channel that is switched off is expected; one that is on and abandoning messages means
  // the owner is not being told about bookings, which is the only failure here worth shouting
  // about on the front page.
  const deadChannels = Object.entries(summary?.notificationChannels ?? {})
    .filter(([, configured]) => !configured)
    .map(([channel]) => channel)

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 3 }}>
        {t('admin.dashboard')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gridAutoRows: '1fr',
          mb: 4,
        }}
      >
        <Stat
          icon="📥"
          value={pending.data?.totalElements ?? 0}
          label={t('admin.pendingRequests')}
          highlight={(pending.data?.totalElements ?? 0) > 0}
        />
        <Stat icon="🛏️" value={summary?.guestsInHouse ?? 0} label={t('admin.guestsInHouse')} />
        <Stat
          icon="🍽️"
          value={summary?.openServiceOrders ?? 0}
          label={t('admin.openOrders')}
          highlight={(summary?.openServiceOrders ?? 0) > 0}
          to="/admin/orders"
        />
        <Stat
          icon="🧾"
          display={moneyPrecise(summary?.outstandingBalance ?? 0)}
          value={0}
          label={t('admin.outstanding')}
          highlight={(summary?.outstandingBalance ?? 0) > 0}
          to="/admin/billing"
        />
        <Stat icon="🚪" value={summary?.arrivingToday ?? 0} label={t('admin.arrivingToday')} />
        <Stat icon="👋" value={summary?.departingToday ?? 0} label={t('admin.departingToday')} />
        <Stat
          icon="📈"
          display={`${Math.round(summary?.occupancyNext30Days ?? 0)}%`}
          value={0}
          label={t('admin.occupancy30')}
        />
        <Stat
          icon={failingFeeds.length > 0 ? '⚠️' : '🔁'}
          value={feeds.data?.length ?? 0}
          label={t('admin.feeds')}
          highlight={failingFeeds.length > 0}
          to="/admin/feeds"
        />
      </Box>

      {deadChannels.length > 0 && (
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2, border: 1, borderColor: 'warning.main', borderRadius: 3 }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            📣 {t('admin.channelsOff', { channels: deadChannels.join(', ') })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('admin.channelsOffBody')}
          </Typography>
        </Paper>
      )}

      {(summary?.notificationsAbandoned ?? 0) > 0 && (
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2, border: 1, borderColor: 'error.main', borderRadius: 3 }}
        >
          <Typography variant="subtitle2" color="error.main">
            ⚠️ {t('admin.notificationsAbandoned', { count: summary?.notificationsAbandoned ?? 0 })}
          </Typography>
        </Paper>
      )}

      {failingFeeds.length > 0 && (
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 4, border: 1, borderColor: 'error.main', borderRadius: 3 }}
        >
          <Typography variant="subtitle2" color="error.main" sx={{ mb: 0.5 }}>
            ⚠️ {failingFeeds.length} channel calendar(s) failed to sync
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Until they sync, nights sold on that channel are not blocked here.
          </Typography>
          <Button component={RouterLink} to="/admin/feeds" size="small" endIcon={<ArrowForwardIcon />}>
            {t('admin.feeds')}
          </Button>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' } }}>
        <Panel
          title={t('admin.pendingRequests')}
          to="/admin/bookings"
          linkLabel={t('admin.bookings')}
        >
          {pending.isError && <ErrorState error={pending.error} />}
          {pending.data?.content.length === 0 && (
            <EmptyState icon="🌿" title={t('admin.noPending')} />
          )}
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {(pending.data?.content ?? []).map((booking) => (
              <Stack
                key={booking.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ py: 1.25, gap: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {booking.guestName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {booking.unitName} · {dateShort(booking.checkIn)} → {dateShort(booking.checkOut)}
                  </Typography>
                </Box>
                <Chip size="small" label={moneyPrecise(booking.totalAmount)} />
              </Stack>
            ))}
          </Stack>
        </Panel>

        <Panel title={t('admin.upcomingArrivals')} to="/admin/calendar" linkLabel={t('admin.calendar')}>
          {upcoming.data?.content.length === 0 && (
            <EmptyState icon="📅" title={t('admin.noPending')} />
          )}
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {(upcoming.data?.content ?? []).slice(0, 6).map((booking) => (
              <Stack
                key={booking.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ py: 1.25, gap: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {booking.unitName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {booking.guestName} · {t('common.guest', { count: booking.adults + booking.children })}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                  {dateShort(booking.checkIn)} → {dateShort(booking.checkOut)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Panel>
      </Box>
    </Box>
  )
}

function Stat({
  icon,
  value,
  display,
  label,
  highlight,
  to,
}: {
  icon: string
  value: number
  /** For figures that are not a plain count - money, a percentage. */
  display?: string
  label: string
  highlight?: boolean
  to?: string
}) {
  return (
    <Paper
      elevation={0}
      {...(to ? { component: RouterLink, to } : {})}
      sx={{
        p: 2.25,
        border: 1,
        borderColor: highlight ? 'primary.main' : 'divider',
        borderRadius: 3,
        bgcolor: highlight ? 'action.hover' : 'background.paper',
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        ...(to ? { '&:hover': { borderColor: 'primary.main' } } : {}),
      }}
    >
      <Box aria-hidden sx={{ fontSize: '1.5rem', mb: 0.5, lineHeight: 1 }}>
        {icon}
      </Box>
      <Typography variant="h3" component="p" sx={{ lineHeight: 1.1 }}>
        {display ?? value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  )
}

function Panel({
  title,
  to,
  linkLabel,
  children,
}: {
  title: string
  to: string
  linkLabel: string
  children: React.ReactNode
}) {
  return (
    <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', borderRadius: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5">{title}</Typography>
        <Button component={RouterLink} to={to} size="small" endIcon={<ArrowForwardIcon />}>
          {linkLabel}
        </Button>
      </Stack>
      {children}
    </Paper>
  )
}
